import React, { useState } from "react";
import { Dialog } from "primereact/dialog";
import { Button } from "primereact/button";
import { InputNumber } from "primereact/inputnumber";
import { Calendar } from "primereact/calendar";
import { Dropdown } from "primereact/dropdown";
import { InputTextarea } from "primereact/inputtextarea";
import { FileUpload } from "primereact/fileupload";
import { ProgressBar } from "primereact/progressbar";
import { Message } from "primereact/message";
import { makeRequest2 } from "../../../../utils/makeRequest";
import { toastify } from "../../../../utils/toast";
import axios from "axios";

const AddPaymentModal = ({
  visible,
  onHide,
  invoice,
  facilityId,
  onPaymentAdded,
}) => {
  const [paymentData, setPaymentData] = useState({
    amountPaid: invoice?.amount || 0,
    paymentDate: new Date(),
    paymentMethod: "cash",
    comments: "",
    proofOfPaymentUrl: "",
  });

  const [uploadedFile, setUploadedFile] = useState(null);
  const [uploading, setUploading] = useState(false);
  const [uploadProgress, setUploadProgress] = useState(0);
  const [submitting, setSubmitting] = useState(false);

  const paymentMethods = [
    { label: "Cash", value: "cash" },
    { label: "Credit Card", value: "credit" },
    { label: "Debit Card", value: "debit" },
  ];

  const UPLOAD_SERVICE_URL =
    process.env.REACT_APP_UPLOAD_SERVICE_URL ||
    "https://api.payserve.co.ke/upload_service";

  const handleFileSelect = async (event) => {
    const file = event.files[0];
    if (!file) return;

    // Validate file size (10MB max)
    if (file.size > 10 * 1024 * 1024) {
      toastify("File size must be less than 10MB", "error");
      return;
    }

    // Validate file type
    const allowedTypes = [
      "image/jpeg",
      "image/png",
      "image/jpg",
      "application/pdf",
    ];
    if (!allowedTypes.includes(file.type)) {
      toastify(
        "Only JPEG, PNG, and PDF files are allowed for proof of payment",
        "error",
      );
      return;
    }

    try {
      setUploading(true);
      setUploadProgress(0);

      // Create form data
      const formData = new FormData();
      formData.append("files", file);

      // Upload to upload service
      const uploadUrl = `${UPLOAD_SERVICE_URL}/api/v1/upload/${facilityId}`;

      const response = await axios.post(uploadUrl, formData, {
        headers: {
          "Content-Type": "multipart/form-data",
        },
        onUploadProgress: (progressEvent) => {
          const progress = Math.round(
            (progressEvent.loaded * 100) / progressEvent.total,
          );
          setUploadProgress(progress);
        },
      });

      if (response.data && response.data.uploaded_files?.length > 0) {
        const uploadedFile = response.data.uploaded_files[0];
        const fileUrl = `${UPLOAD_SERVICE_URL}/api/v1/file/${facilityId}/${uploadedFile.unique_filename}/download`;

        setPaymentData({
          ...paymentData,
          proofOfPaymentUrl: fileUrl,
        });

        setUploadedFile({
          name: uploadedFile.original_name,
          url: fileUrl,
          viewUrl: `${UPLOAD_SERVICE_URL}/api/v1/file/${facilityId}/${uploadedFile.unique_filename}/view`,
        });

        toastify("Proof of payment uploaded successfully!", "success");
      } else {
        throw new Error("Upload service did not return file information");
      }
    } catch (error) {
      console.error("Upload error:", error);
      toastify(
        `Failed to upload proof: ${error.response?.data?.detail || error.message}`,
        "error",
      );
      setPaymentData({
        ...paymentData,
        proofOfPaymentUrl: "",
      });
      setUploadedFile(null);
    } finally {
      setUploading(false);
      setUploadProgress(0);
    }
  };

  const handleRemoveFile = () => {
    setUploadedFile(null);
    setPaymentData({
      ...paymentData,
      proofOfPaymentUrl: "",
    });
  };

  const handleSubmit = async () => {
    // Validate required fields
    if (!paymentData.amountPaid || paymentData.amountPaid <= 0) {
      toastify("Please enter a valid payment amount", "error");
      return;
    }

    if (!paymentData.paymentDate) {
      toastify("Please select a payment date", "error");
      return;
    }

    try {
      setSubmitting(true);

      const payload = {
        amountPaid: paymentData.amountPaid,
        paymentDate: paymentData.paymentDate.toISOString().split("T")[0],
        paymentMethod: paymentData.paymentMethod,
        comments: paymentData.comments,
        proofOfPaymentUrl: paymentData.proofOfPaymentUrl,
      };

      const endpoint = `/api/core/billing/facility/${facilityId}/invoices/${invoice._id}/payments`;

      const response = await makeRequest2(endpoint, "POST", payload);

      // Check if response is successful (could have success flag or just be a successful response)
      if (response && (response.success !== false || response.payment)) {
        const message = response.message || "Payment added successfully!";
        toastify(message, "success");
        if (onPaymentAdded) {
          onPaymentAdded(response);
        }
        handleClose();
      } else {
        const errorMsg =
          response?.error || response?.details || "Failed to add payment";

        // Check if the error is actually a 201 (Created) response
        if (errorMsg.includes("201")) {
          toastify("Payment added successfully!", "success");
          if (onPaymentAdded) {
            onPaymentAdded({ success: true });
          }
          handleClose();
        } else {
          toastify(errorMsg, "error");
        }
      }
    } catch (error) {
      console.error("Payment submission error:", error);

      // Check if it's actually a 201 (Created) response that makeRequest2 treats as error
      if (error.message && error.message.includes("201")) {
        toastify("Payment added successfully!", "success");
        if (onPaymentAdded) {
          onPaymentAdded({ success: true });
        }
        handleClose();
      } else {
        // Extract meaningful error message
        const errorMessage =
          error.response?.data?.error ||
          error.response?.data?.detail ||
          error.response?.data?.message ||
          error.message ||
          "An unexpected error occurred";
        toastify(`Failed to add payment: ${errorMessage}`, "error");
      }
    } finally {
      setSubmitting(false);
    }
  };

  const handleClose = () => {
    setPaymentData({
      amountPaid: invoice?.amount || 0,
      paymentDate: new Date(),
      paymentMethod: "cash",
      comments: "",
      proofOfPaymentUrl: "",
    });
    setUploadedFile(null);
    setUploading(false);
    setUploadProgress(0);
    onHide();
  };

  const remainingBalance = invoice
    ? invoice.amount - (invoice.totalPaid || 0)
    : 0;

  return (
    <Dialog
      visible={visible}
      onHide={handleClose}
      header="Add Payment"
      modal
      style={{ width: "600px" }}
      footer={
        <div className="flex justify-content-end gap-2">
          <Button
            label="Cancel"
            icon="ti ti-x"
            className="p-button-text"
            onClick={handleClose}
            disabled={submitting || uploading}
          />
          <Button
            label="Submit Payment"
            icon="ti ti-check"
            onClick={handleSubmit}
            disabled={submitting || uploading}
            loading={submitting}
          />
        </div>
      }
    >
      <div className="p-fluid">
        {/* Invoice Information */}
        <div className="mb-4 p-3 bg-light rounded">
          <h6 className="mb-2">
            <i className="ti ti-receipt me-2"></i>Invoice Details
          </h6>
          <div className="grid">
            <div className="col-6">
              <small className="text-muted">Invoice Number:</small>
              <div className="fw-bold">{invoice?.invoiceNumber}</div>
            </div>
            <div className="col-6">
              <small className="text-muted">Total Amount:</small>
              <div className="fw-bold">
                KES {invoice?.amount?.toLocaleString()}
              </div>
            </div>
            {invoice?.totalPaid > 0 && (
              <>
                <div className="col-6">
                  <small className="text-muted">Already Paid:</small>
                  <div className="text-success fw-bold">
                    KES {invoice?.totalPaid?.toLocaleString()}
                  </div>
                </div>
                <div className="col-6">
                  <small className="text-muted">Remaining Balance:</small>
                  <div className="text-warning fw-bold">
                    KES {remainingBalance?.toLocaleString()}
                  </div>
                </div>
              </>
            )}
          </div>
        </div>

        {/* Payment Amount */}
        <div className="field mb-3">
          <label htmlFor="amountPaid" className="fw-bold">
            Payment Amount <span className="text-danger">*</span>
          </label>
          <InputNumber
            id="amountPaid"
            value={paymentData.amountPaid}
            onValueChange={(e) =>
              setPaymentData({ ...paymentData, amountPaid: e.value })
            }
            mode="currency"
            currency="KES"
            locale="en-KE"
            placeholder="Enter payment amount"
            className="w-100"
            disabled={submitting}
          />
          <small className="text-muted">
            Suggested: KES {remainingBalance?.toLocaleString()}
          </small>
        </div>

        {/* Payment Date */}
        <div className="field mb-3">
          <label htmlFor="paymentDate" className="fw-bold">
            Payment Date <span className="text-danger">*</span>
          </label>
          <Calendar
            id="paymentDate"
            value={paymentData.paymentDate}
            onChange={(e) =>
              setPaymentData({ ...paymentData, paymentDate: e.value })
            }
            dateFormat="yy-mm-dd"
            placeholder="Select payment date"
            maxDate={new Date()}
            showIcon
            className="w-100"
            disabled={submitting}
          />
        </div>

        {/* Payment Method */}
        <div className="field mb-3">
          <label htmlFor="paymentMethod" className="fw-bold">
            Payment Method
          </label>
          <Dropdown
            id="paymentMethod"
            value={paymentData.paymentMethod}
            options={paymentMethods}
            onChange={(e) =>
              setPaymentData({ ...paymentData, paymentMethod: e.value })
            }
            placeholder="Select payment method"
            className="w-100"
            disabled={submitting}
          />
        </div>

        {/* Proof of Payment Upload */}
        <div className="field mb-3">
          <label className="fw-bold">Proof of Payment</label>
          {!uploadedFile ? (
            <>
              <FileUpload
                mode="basic"
                name="file"
                accept="image/*,application/pdf"
                maxFileSize={10485760}
                onSelect={handleFileSelect}
                chooseLabel="Upload Proof"
                className="w-100"
                disabled={uploading || submitting}
                auto={false}
              />
              <small className="text-muted">
                Upload receipt, bank statement, or payment confirmation (JPEG,
                PNG, PDF - Max 10MB)
              </small>
              {uploading && (
                <div className="mt-2">
                  <ProgressBar value={uploadProgress} />
                  <small className="text-muted">Uploading...</small>
                </div>
              )}
            </>
          ) : (
            <div className="border rounded p-3 bg-light">
              <div className="flex justify-content-between align-items-center">
                <div className="flex align-items-center gap-2">
                  <i className="ti ti-file-check text-success fs-4"></i>
                  <div>
                    <div className="fw-bold">{uploadedFile.name}</div>
                    <small className="text-success">
                      <i className="ti ti-check me-1"></i>Uploaded successfully
                    </small>
                  </div>
                </div>
                <div className="flex gap-2">
                  <Button
                    icon="ti ti-eye"
                    className="p-button-sm p-button-text"
                    onClick={() => window.open(uploadedFile.viewUrl, "_blank")}
                    tooltip="Preview"
                  />
                  <Button
                    icon="ti ti-trash"
                    className="p-button-sm p-button-text p-button-danger"
                    onClick={handleRemoveFile}
                    tooltip="Remove"
                    disabled={submitting}
                  />
                </div>
              </div>
            </div>
          )}
        </div>

        {/* Comments */}
        <div className="field mb-3">
          <label htmlFor="comments" className="fw-bold">
            Comments / Notes
          </label>
          <InputTextarea
            id="comments"
            value={paymentData.comments}
            onChange={(e) =>
              setPaymentData({ ...paymentData, comments: e.target.value })
            }
            rows={3}
            placeholder="Add any additional notes about this payment"
            className="w-100"
            disabled={submitting}
          />
        </div>

        {/* Warning Messages */}
        {!paymentData.proofOfPaymentUrl && (
          <Message
            severity="warn"
            text="Consider uploading proof of payment for better record keeping"
            className="w-100 mb-2"
          />
        )}
        {paymentData.amountPaid > remainingBalance && (
          <Message
            severity="info"
            text={`This payment exceeds the remaining balance by KES ${(paymentData.amountPaid - remainingBalance).toLocaleString()}`}
            className="w-100"
          />
        )}
      </div>
    </Dialog>
  );
};

export default AddPaymentModal;
